// src/components/ceremony/CeremonyStage.js
// ============================================================================
// The single thing EventWatch mounts. It owns the founder's whole sequence:
//
//   registration closes -> countdown
//   T-5                 -> STARTING SOON + music + live per-IGN ready ticker
//   field complete      -> GRID LOCKED
//   T-30                -> crossfade out, feeds take the screen
//   per POV terminal    -> that camera's result / awaiting-results card
//   all results in      -> the Crits ceremony
//
// It renders INSIDE the video area, over the player. Two rules it never breaks:
//   1. It never blocks the picture once the race is running — the full-bleed
//      b-roll only exists pre-live and post-completion. During LIVE the only
//      thing it can put on screen is a single-camera result card.
//   2. It never swallows input it doesn't own: everything is pointerEvents
//      "box-none"/"none" except the two controls it actually offers (sound,
//      switch camera), so the player's own overlay keeps working underneath.
// ============================================================================

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';

import {
  CEREMONY_PHASE,
  POV_STATE,
  HANDOFF_FADE_MS,
  CEREMONY_COLORS as C,
} from '../../constants/ceremony';
import StartingSoonCeremony from './StartingSoonCeremony';
import GridLockCeremony from './GridLockCeremony';
import ResultsCeremony from './ResultsCeremony';
import PovResultOverlay from './PovResultOverlay';
import useCeremonyAudio from './useCeremonyAudio';
import { anim } from './CeremonyCanvas';

const FULL_BLEED = new Set([
  CEREMONY_PHASE.COUNTDOWN,
  CEREMONY_PHASE.STARTING_SOON,
  CEREMONY_PHASE.GRID_LOCK,
  CEREMONY_PHASE.RESULTS,
]);

// Past every beat of the grid-lock timeline — used to hold that screen at its
// finished state while it dissolves.
const FROZEN_ELAPSED_MS = 60000;

// Slack after the dissolve completes before the stage stops rendering at all.
const HANDOFF_GRACE_MS = 400;

// Longest the b-roll will wait for a picture AFTER the scheduled start before
// giving the screen back. Generous on purpose: EventWatch polls getEventStreams
// every 8s while nothing is live, relay->HLS latency measured 34s on the first
// live run (2026-08-07), and HLS needs to buffer on top of that — a short bound
// would cut the sequence exactly when it's about to pay off.
//
// ⚠️ Measured from startAt, NOT from when HANDOFF began. The lifecycle starts an
// event as soon as everyone is ready and verification clears — 69s before
// startAt on that run, and potentially much earlier — which would otherwise
// burn this whole budget waiting for a race that hasn't been scheduled to
// begin yet, then uncover a black player right at the money shot.
const HANDOFF_MAX_HOLD_MS = 90000;

// Mirrors EventWatchScreen's COMPACT_BAR_HEIGHT (that constant is module-local
// to a 4700-line screen). If it moves there, move it here.
const COMPACT_BAR_HEIGHT = 60;

// The T-30 dissolve. On native (and under reduced motion) anim() returns null
// and the layer just goes, which is the same outcome without the fade.
const fadeOutStyle = anim('fadeOut', { duration: HANDOFF_FADE_MS, easing: 'ease-in' })
  || { opacity: 0 };

export default function CeremonyStage({ ceremony, eventId, onSwitchCamera, canSwitchCamera }) {
  const {
    phase,
    phaseElapsedMs,
    feedReady,
    dissolveElapsedMs,
    field,
    standings,
    povState,
    readyCount,
    totalRequired,
    msUntilStart,
    registrationClosed,
    entryCrits,
    netPoolCrits,
    payingPositions,
    rumbleTotalCrits,
    rumbleCritsEach,
    rumbleCount,
    title,
    state,
    resultsFinal,
  } = ceremony;

  const fullBleed = FULL_BLEED.has(phase);

  // THE HANDOFF IS DRIVEN BY THE PICTURE, NOT BY A CLOCK.
  //
  // The b-roll dissolves to REVEAL the player underneath it (same DOM, nothing
  // remounts, no reload). So it must not start fading until there is something
  // to reveal: streams resolve on their own poll and HLS needs a moment to
  // buffer after that, so a timer-driven fade would uncover a black element and
  // put a visible gap in the middle of the sequence.
  //
  // Hold while HANDOFF has no picture, dissolve once one is painting, unmount
  // when the dissolve is spent — but never hold forever. If the event went live
  // and nobody ever streamed, stop covering the player and let its own
  // "waiting for players to go live" placeholder tell the truth.
  // Before startAt, holding is unconditionally correct — the race is not even
  // due yet. Only after it starts does an absent picture become a reason to
  // give the screen back.
  const msSinceStart = typeof msUntilStart === 'number' ? -msUntilStart : null;
  const withinHoldBudget = msSinceStart == null || msSinceStart < HANDOFF_MAX_HOLD_MS;
  const holding = phase === CEREMONY_PHASE.HANDOFF
    && !feedReady
    && withinHoldBudget;
  const dissolving = phase === CEREMONY_PHASE.HANDOFF && feedReady;
  const handing = holding
    || (dissolving && dissolveElapsedMs < HANDOFF_FADE_MS + HANDOFF_GRACE_MS);

  // The bed keeps playing for as long as the b-roll is actually on screen. If
  // audio were keyed to the raw phase it would cut the moment HANDOFF began and
  // leave the held screen sitting in silence while we wait for a picture.
  const audioPhase = holding ? CEREMONY_PHASE.GRID_LOCK : phase;
  const { soundOn, toggleSound, canOfferSound } = useCeremonyAudio(audioPhase);

  // Belt and braces on the ship-dark flag. OFF means OFF for every surface —
  // the POV card is a full-player overlay, and gating only the full-bleed
  // phases would still let it take the picture on a flag-off event.
  if (phase === CEREMONY_PHASE.OFF) return null;

  // Nothing to stage, and no camera to annotate.
  if (!fullBleed && !handing && (!povState || povState.state === POV_STATE.RACING)) {
    return null;
  }

  const strapline = state?.strapline || null;

  let screen = null;
  if (phase === CEREMONY_PHASE.COUNTDOWN || phase === CEREMONY_PHASE.STARTING_SOON) {
    screen = (
      <StartingSoonCeremony
        phase={phase}
        title={title}
        strapline={strapline}
        field={field}
        readyCount={readyCount}
        totalRequired={totalRequired}
        msUntilStart={msUntilStart}
        registrationClosed={registrationClosed}
        eventId={eventId}
      />
    );
  } else if (phase === CEREMONY_PHASE.GRID_LOCK) {
    screen = (
      <GridLockCeremony
        title={title}
        strapline={strapline}
        field={field}
        entryCrits={entryCrits}
        phaseElapsedMs={phaseElapsedMs}
      />
    );
  } else if (handing) {
    // The dissolve has to dissolve FROM something. Hold the grid-lock screen,
    // frozen at its stamped end state, so T-30 reads as the b-roll giving way
    // to the cameras rather than a cut to black.
    screen = (
      <GridLockCeremony
        title={title}
        strapline={strapline}
        field={field}
        entryCrits={entryCrits}
        phaseElapsedMs={FROZEN_ELAPSED_MS}
      />
    );
  } else if (phase === CEREMONY_PHASE.RESULTS) {
    screen = (
      <ResultsCeremony
        title={title}
        strapline={strapline}
        standings={standings}
        entryCrits={entryCrits}
        netPoolCrits={netPoolCrits}
        resultsFinal={resultsFinal}
        payingPositions={payingPositions}
        rumbleTotalCrits={rumbleTotalCrits}
        rumbleCritsEach={rumbleCritsEach}
        rumbleCount={rumbleCount}
        mode={state?.mode || null}
        phaseElapsedMs={phaseElapsedMs}
        eventId={eventId}
      />
    );
  }

  return (
    <View style={styles.root} pointerEvents="box-none">
      {/* T-30: the b-roll dissolves and the feeds come up underneath. The
          fading layer keeps the LAST staged screen so the dissolve has
          something to dissolve FROM. */}
      {(fullBleed || handing) && (
        <View
          style={[styles.layer, dissolving ? fadeOutStyle : null]}
          // 'auto', not 'box-none': the b-roll is opaque and covers the whole
          // player, so it has to ABSORB clicks. Left transparent, a click on
          // the ceremony reached the play/pause and mute controls hidden
          // underneath it. The fading handoff layer stays inert.
          pointerEvents={fullBleed ? 'auto' : 'none'}
        >
          {screen || <HandoffCurtain />}
        </View>
      )}

      {/* One camera's result — lives over the player, never over the others. */}
      {!fullBleed && (
        <PovResultOverlay
          pov={povState}
          onSwitchCamera={onSwitchCamera}
          canSwitch={!!canSwitchCamera}
        />
      )}

      {canOfferSound && (
        <Pressable
          style={({ pressed }) => [styles.soundBtn, pressed && styles.soundBtnPressed]}
          onPress={toggleSound}
          accessibilityRole="button"
          accessibilityLabel={soundOn ? 'Mute ceremony music' : 'Turn on ceremony music'}
        >
          <Text style={styles.soundText}>{soundOn ? 'SOUND ON' : 'SOUND OFF'}</Text>
        </Pressable>
      )}
    </View>
  );
}

/** The half-second between "b-roll is done" and "a feed actually resolves". */
function HandoffCurtain() {
  return (
    <View style={styles.curtain} pointerEvents="none">
      <Text style={[styles.curtainText, anim('fadeIn', { duration: 500 })]}>
        GOING LIVE
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 14, // under CompactInfoBar (20), over the player + its overlay (10)
    // CompactInfoBar is an OPAQUE strip pinned to the bottom of #video-area.
    // Without this inset the ceremony's design floor — the sponsor slot, the
    // results footer — renders underneath it and is simply never seen.
    bottom: COMPACT_BAR_HEIGHT,
  },
  layer: StyleSheet.absoluteFillObject,
  curtain: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#000000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  curtainText: {
    fontSize: 28,
    fontWeight: '900',
    letterSpacing: 12,
    color: C.GOLD,
  },
  // NOT top-right. EventWatch's floatingTopBar is a SIBLING of #video-area at
  // zIndex 100, full width and ~44px tall, with no pointerEvents="box-none" —
  // and every react-native-web View emits `position:relative; z-index:0`, so
  // #video-area is a z-0 stacking context and nothing inside it can paint or
  // hit-test above that bar. A control up there is ~86% dead, and this button
  // is the ONLY way to turn the music on (nothing autoplays audibly by design).
  // Bottom-left, clear of CompactInfoBar, is the free corner.
  soundBtn: {
    position: 'absolute',
    bottom: 72,
    left: 14,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: '#3a3a3a',
    backgroundColor: 'rgba(0,0,0,0.65)',
    zIndex: 30,
  },
  soundBtnPressed: { borderColor: C.GOLD },
  soundText: { fontSize: 12, fontWeight: '900', letterSpacing: 2, color: C.TEXT },
});
